import { batch, observable } from '@legendapp/state';
import { z } from 'zod';
import { configurationState$ } from './configuration.state';
import { towersState$ } from './towers.state';

export enum AutoBetOnWinLossAction {
  RESET = 'RESET',
  INCREASE_OR_DECREASE = 'INCREASE_OR_DECREASE',
}

export const AutoBetConfigurationSchema = z.object({
  onWinAction: z.enum(AutoBetOnWinLossAction),
  onWinIncreaseValue: z.number().max(1000).min(-1000),
  onLossAction: z.enum(AutoBetOnWinLossAction),
  onLossIncreaseValue: z.number().max(1000).min(-1000),
  stopOnProfitValue: z.number(),
  stopOnLossValue: z.number(),
});

export const DEFAULT_AUTO_BET_CONFIGURATION: z.infer<
  typeof AutoBetConfigurationSchema
> = {
  onWinAction: AutoBetOnWinLossAction.RESET,
  onWinIncreaseValue: 0,
  onLossAction: AutoBetOnWinLossAction.RESET,
  onLossIncreaseValue: 0,
  stopOnProfitValue: 0,
  stopOnLossValue: 0,
};

type AutoBetPickedBrick = {
  row: number;
  column: number;
};

type AutobetState = {
  betAmount: number;
  numberOfBets: number;
  isAutoBetting: boolean;
  isBetting: boolean;
  // Additional properties for autobetting system
  runningCurrentBetCount: number;
  runningTotalProfit: number;
  hasInfiniteBets: boolean;
  initialBetAmount: number;

  setBetAmount: (betAmount: number) => void;
  setIsAutoBetting: (isAutoBetting: boolean) => void;
  setIsBetting: (isBetting: boolean) => void;
  startAutoBetting: () => void;
  stopAutoBetting: () => void;
  resetAutoBetStats: () => void;
  pickedBricks: number[];
  resetPickedBricks: () => void;
  handlePickedBrick: (brick: AutoBetPickedBrick) => void;
  // modal
  isConfigureAutoBetModalOpen: boolean;
  openConfigureAutoBetModal: () => void;
  closeConfigureAutoBetModal: () => void;
  toggleConfigureAutoBetModal: () => void;
  saveAutoBetConfiguration: (
    configuration: z.infer<typeof AutoBetConfigurationSchema>
  ) => void;
} & z.infer<typeof AutoBetConfigurationSchema>;

export const autoBetState$ = observable<AutobetState>({
  ...DEFAULT_AUTO_BET_CONFIGURATION,
  betAmount: 0,
  numberOfBets: 10,
  isAutoBetting: false,
  isBetting: false,
  // Additional properties for autobetting system
  runningCurrentBetCount: 0,
  runningTotalProfit: 0,
  hasInfiniteBets: false,
  initialBetAmount: 0,
  // modal
  isConfigureAutoBetModalOpen: false,

  setBetAmount: (betAmount: number) => {
    // Validate input
    if (
      Number.isNaN(betAmount) ||
      !Number.isFinite(betAmount) ||
      betAmount < 0
    ) {
      betAmount = 0;
    }
    const maxBetAmount = configurationState$.maxBet.peek();
    const minBetAmount = configurationState$.minBet.peek();
    const cappedBetAmount = Math.min(
      Math.max(betAmount, minBetAmount),
      maxBetAmount
    );
    autoBetState$.betAmount.set(cappedBetAmount);
  },
  saveAutoBetConfiguration: (
    configuration: z.infer<typeof AutoBetConfigurationSchema>
  ) => {
    autoBetState$.assign({
      ...configuration,
      isConfigureAutoBetModalOpen: false,
    });
  },
  openConfigureAutoBetModal: () => {
    autoBetState$.isConfigureAutoBetModalOpen.set(true);
  },
  closeConfigureAutoBetModal: () => {
    autoBetState$.isConfigureAutoBetModalOpen.set(false);
  },
  toggleConfigureAutoBetModal: () => {
    autoBetState$.isConfigureAutoBetModalOpen.set((prev) => !prev);
  },
  setIsAutoBetting: (isAutoBetting: boolean) => {
    autoBetState$.isAutoBetting.set(isAutoBetting);
  },
  setIsBetting: (isBetting: boolean) => {
    autoBetState$.isBetting.set(isBetting);
  },
  startAutoBetting: () => {
    batch(() => {
      const betAmount = autoBetState$.betAmount.peek();

      autoBetState$.initialBetAmount.set(betAmount);
      autoBetState$.isAutoBetting.set(true);
      autoBetState$.runningCurrentBetCount.set(0);
      autoBetState$.runningTotalProfit.set(0);
    });
  },
  stopAutoBetting: () => {
    towersState$.resetBricks();
    autoBetState$.isAutoBetting.set(false);
    autoBetState$.isBetting.set(false);
  },
  resetAutoBetStats: () => {
    batch(() => {
      autoBetState$.runningCurrentBetCount.set(0);
      autoBetState$.runningTotalProfit.set(0);
    });
  },
  pickedBricks: [],
  resetPickedBricks: () => {
    autoBetState$.pickedBricks.set([]);
  },
  handlePickedBrick: ({ column, row }: AutoBetPickedBrick) => {
    autoBetState$.pickedBricks.set((prev) => {
      const newPickedBricks = [...prev];

      // If the clicked row already has this column selected
      if (newPickedBricks[row] === column) {
        // Remove selections from this row and all rows above it (higher indices)
        // Slice the array to remove elements from this row onwards
        return newPickedBricks.slice(0, row);
      }
      // Check if we can select this row (must pick from bottom to top)
      // Row 0 (bottom) can always be picked
      // Any other row can only be picked if the previous row has been selected
      if (row > 0 && newPickedBricks.length <= row - 1) {
        // Block selection - previous row not picked yet
        return prev;
      }

      // Normal selection - set the column for this row
      newPickedBricks[row] = column;
      return newPickedBricks;
    });
  },
});
